import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Icons } from "../../constants/icon-library";
import { upsertParticipant } from "../../features/participant/participantSlice";
import {
  CREATE_PARTICIPANT_INITIAL_VALUES,
  createParticipantValidationSchema,
} from "../../constants/participant.constant";
import MultiSelect from "../../components/common/MultiSelect";

const AddParticipantModal = ({
  show,
  onHide,
  onParticipantAdded,
  settings,
}) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.participants);

  const uniquePlatforms = [
    ...new Map(
      (settings || []).map((item) => [
        item?.assistantId?.platform?.uuid,
        {
          label: item?.assistantId?.platform?.name,
          value: item?.assistantId?.platform?.type,
        },
      ]),
    ).values(),
  ];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const result = await dispatch(
        upsertParticipant({
          name: values.name.trim(),
          username: values.username.trim(),
          platforms: values.platforms,
        }),
      );
      if (result.payload?.uuid) {
        toast.success("Participant created successfully");

        if (onParticipantAdded) {
          onParticipantAdded(result.payload);
        }

        resetForm();
        onHide();
      } else {
        toast.error(result.payload?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to save participant");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-md">
        <div className="modal-content">
          <div className="modal-header border-bottom d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <i className={`wb ${Icons("add")} fs-8`} />
              <h5 className="modal-title mb-0">Add Participant</h5>
            </div>
            <div className="d-flex justify-content-center">
              <button
                type="button"
                className="btn-close"
                onClick={onHide}
                aria-label="Close"
              />
            </div>
          </div>

          <div className="modal-body">
            <Formik
              initialValues={CREATE_PARTICIPANT_INITIAL_VALUES}
              validationSchema={createParticipantValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, setFieldValue, values }) => (
                <Form>
                  {/* Name Field */}
                  <div className="mb-4">
                    <label className="fw-bold mb-2">
                      Name <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="Enter participant name"
                    />
                    <div className="text-danger small mt-1">
                      <ErrorMessage name="name" />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="mb-4">
                    <label className="fw-bold mb-2">
                      Email/Username <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="username"
                      className="form-control"
                      placeholder="Enter participant username"
                    />
                    <div className="text-danger small mt-1">
                      <ErrorMessage name="username" />
                    </div>
                  </div>

                  {/* Platform Field */}
                  <div className="mb-4">
                    <label className="fw-bold mb-2">
                      Platform <span className="text-danger">*</span>
                    </label>
                    <MultiSelect
                      options={uniquePlatforms}
                      value={values.platforms || []}
                      onChange={(value) => setFieldValue("platforms", value)}
                      placeholder="Select platform..."
                    />

                    <div className="text-danger small">
                      <ErrorMessage name="platforms" />
                    </div>
                  </div>

                  {/* Footer Buttons */}
                  <div className="modal-footer border-top px-0 d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={onHide}
                      disabled={loading || isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={loading || isSubmitting}
                    >
                      {loading || isSubmitting
                        ? "Saving..."
                        : "Save Participant"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddParticipantModal;
